import {
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  Clock3,
  RefreshCw,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatCompact, formatMoney, marketIsStale } from "./market";
import type { CoinMarketRow, HistoryDays, HistoryResponse, OverviewResponse } from "./types";

const RANGE_LABELS: Record<HistoryDays, string> = {
  1: "1T",
  7: "7T",
  30: "30T",
  90: "3M",
  365: "1J",
};

export function CryptoStatus({
  data,
  refreshing,
  refresh,
}: {
  data: OverviewResponse;
  refreshing: boolean;
  refresh: () => void;
}) {
  const stale = data.stale || marketIsStale(data.timestamp);
  const age = Math.max(0, Math.floor((Date.now() - data.timestamp * 1000) / 60000));
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-white/40">
      <span className={`flex items-center gap-1.5 ${stale ? "text-[#E53935]" : "text-[#1FBF75]"}`}>
        {stale ? <Clock3 className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
        {stale ? "Daten möglicherweise veraltet" : age < 1 ? "Gerade aktualisiert" : `Aktualisiert vor ${age} Min.`}
      </span>
      <button
        onClick={refresh}
        className="flex h-9 items-center gap-2 border border-white/10 bg-[#07080d] px-3 font-medium text-white"
      >
        <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
        Aktualisieren
      </button>
    </div>
  );
}

function ChangeBadge({ value }: { value: number | null }) {
  if (value === null) return <span className="text-xs text-white/40">–</span>;
  const positive = value >= 0;
  return (
    <span className={`flex items-center gap-1 text-xs font-semibold ${positive ? "text-[#1FBF75]" : "text-[#E53935]"}`}>
      {positive ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
      {value.toLocaleString("de-DE", { maximumFractionDigits: 2 })}%
    </span>
  );
}

export function CoinOverviewCard({
  coin,
  selected,
  onSelect,
}: {
  coin: CoinMarketRow;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`border p-5 text-left transition-colors ${selected ? "border-[#e01e37] bg-[#07080d]" : "border-white/10 bg-[#07080d] hover:border-white/20"}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          {coin.image && <img src={coin.image} alt="" className="h-8 w-8" />}
          <div>
            <p className="text-sm font-semibold text-white">{coin.name}</p>
            <p className="mt-0.5 font-mono text-xs text-white/40">{coin.symbol}</p>
          </div>
        </div>
        <ChangeBadge value={coin.changePercent24h} />
      </div>
      <p className="mt-6 font-display text-2xl font-bold tabular-nums text-white">
        {coin.price !== null ? formatMoney(coin.price) : "–"}
      </p>
      <div className="mt-4 grid grid-cols-2 gap-2 text-[11px] text-white/40">
        <span>7T: {coin.changePercent7d !== null ? `${coin.changePercent7d.toLocaleString("de-DE", { maximumFractionDigits: 2 })}%` : "–"}</span>
        <span>Vol. 24h: {coin.volume24h !== null ? formatCompact(coin.volume24h) : "–"}</span>
        <span className="col-span-2">Market Cap: {coin.marketCap !== null ? formatCompact(coin.marketCap) : "–"}</span>
      </div>
    </button>
  );
}

export function CoinPriceChart({
  history,
  loading,
  days,
  onDays,
}: {
  history: HistoryResponse | null;
  loading: boolean;
  days: HistoryDays;
  onDays: (d: HistoryDays) => void;
}) {
  const series = (history?.points ?? []).map((p) => ({
    date: new Date(p.timestamp * 1000).toLocaleDateString("de-DE"),
    price: p.price,
  }));
  return (
    <section className="border border-white/10 bg-[#07080d] p-5 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between">
        <div>
          <h2 className="font-display text-xl font-bold text-white">Kursverlauf</h2>
          <p className="mt-1 text-sm text-white/40">Echte historische Kurse · keine Interpolation</p>
        </div>
        <div className="flex border border-white/10">
          {(Object.keys(RANGE_LABELS) as unknown as HistoryDays[]).map((range) => (
            <button
              key={range}
              type="button"
              onClick={() => onDays(range)}
              className={`h-10 px-3 text-xs font-semibold ${Number(range) === days ? "bg-[#e01e37] text-white" : "text-white/40"}`}
            >
              {RANGE_LABELS[range]}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="mt-6 h-96 animate-pulse bg-white/[.03]" />
      ) : series.length > 1 ? (
        <div className="mt-6 h-96 min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series}>
              <defs>
                <linearGradient id="cryptoChart" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#e01e37" stopOpacity=".24" />
                  <stop offset="1" stopColor="#e01e37" stopOpacity="0" />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,.08)" vertical={false} />
              <XAxis dataKey="date" stroke="#555" tickLine={false} axisLine={false} minTickGap={40} />
              <YAxis stroke="#555" tickLine={false} axisLine={false} width={68} domain={["auto", "auto"]} />
              <Tooltip
                formatter={(v) => [formatMoney(Number(v)), "Preis"]}
                contentStyle={{ background: "#0a0b10", border: "1px solid rgba(255,255,255,.12)", borderRadius: 0, color: "#fff" }}
              />
              <Area type="monotone" dataKey="price" stroke="#e01e37" strokeWidth={2} fill="url(#cryptoChart)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="mt-8 border border-dashed border-white/10 bg-white/[.025] p-10 text-center text-sm text-white/40">
          Keine historischen Daten verfügbar.
        </div>
      )}
    </section>
  );
}

function CoinTable({ title, rows }: { title: string; rows: CoinMarketRow[] }) {
  return (
    <section className="border border-white/10 bg-[#07080d]">
      <div className="border-b border-white/10 p-5">
        <h2 className="font-display text-lg font-bold">{title}</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px] text-left text-sm">
          <thead className="bg-white/[.025] text-xs uppercase tracking-wider text-white/40">
            <tr>
              <th className="p-4">Coin</th>
              <th className="p-4">Preis</th>
              <th className="p-4">24h</th>
              <th className="p-4">Market Cap</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {rows.map((coin) => (
              <tr key={coin.id} className="hover:bg-white/[.025]">
                <td className="flex items-center gap-2 p-4 font-semibold">
                  {coin.image && <img src={coin.image} alt="" className="h-5 w-5" />}
                  {coin.name}
                  <span className="font-mono text-xs text-white/40">{coin.symbol}</span>
                </td>
                <td className="p-4 tabular-nums">{coin.price !== null ? formatMoney(coin.price) : "–"}</td>
                <td className="p-4">
                  <ChangeBadge value={coin.changePercent24h} />
                </td>
                <td className="p-4 tabular-nums text-white/60">
                  {coin.marketCap !== null ? formatCompact(coin.marketCap) : "–"}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={4} className="p-6 text-center text-white/40">
                  Keine Daten verfügbar.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function TopCoinsTable({ rows }: { rows: CoinMarketRow[] }) {
  return <CoinTable title="Top Coins" rows={rows} />;
}
export function TopGainersTable({ rows }: { rows: CoinMarketRow[] }) {
  return <CoinTable title="Top Gainer (24h)" rows={rows} />;
}
export function TopLosersTable({ rows }: { rows: CoinMarketRow[] }) {
  return <CoinTable title="Top Loser (24h)" rows={rows} />;
}
